<script setup>
import { onMounted, ref, watch } from "vue";
import { useRoute } from "vue-router";
import LayoutSidebar from "@/views/Layout/LayoutSidebar.vue";
import LayoutNav from "@/views/Layout/LayoutNav.vue";

const route = useRoute();
const sidebarOpen = ref(false);
const sidebarCompact = ref(false);
const SIDEBAR_COMPACT_STORAGE_KEY = "neptune_sidebar_compact";

const readSidebarCompactPreference = () => {
  if (typeof window === "undefined") {
    return false;
  }

  try {
    const storedValue = window.localStorage.getItem(SIDEBAR_COMPACT_STORAGE_KEY);
    if (storedValue === "true") {
      return true;
    }
    if (storedValue === "false") {
      return false;
    }
  } catch {
    // Fall back to viewport heuristics when storage is unavailable.
  }

  return window.innerWidth <= 1366;
};

const toggleSidebar = () => {
  sidebarOpen.value = !sidebarOpen.value;
};

const toggleSidebarCompact = () => {
  sidebarCompact.value = !sidebarCompact.value;
};

const closeSidebar = () => {
  sidebarOpen.value = false;
};

watch(sidebarCompact, (value) => {
  if (typeof window === "undefined") {
    return;
  }

  try {
    window.localStorage.setItem(SIDEBAR_COMPACT_STORAGE_KEY, String(value));
  } catch {
    // Ignore storage failures and keep the in-memory preference.
  }
});

onMounted(() => {
  sidebarCompact.value = readSidebarCompactPreference();

  // Login navigation belongs to the login view and router guard. This layout
  // can mount before that navigation finishes, while the route is still /login.
});
</script>
<template>
  <div class="min-h-screen lg:h-screen flex flex-col m-0 p-0 bg-slate-100 dark:bg-slate-700 lg:overflow-hidden">
    <div class="lg:sticky lg:top-0 lg:z-30">
      <LayoutNav :sidebarOpen="sidebarOpen" @toggle-sidebar="toggleSidebar" />
    </div>

    <div class="flex flex-col md:flex-row flex-1 min-h-0 min-w-0">
      <div
        v-if="sidebarOpen"
        class="fixed inset-0 bg-slate-900/40 z-30 md:hidden"
        @click="closeSidebar"
      ></div>
      <div
        class="w-full shrink-0 border-b border-slate-200 px-3 pt-3 dark:bg-slate-800 dark:border-slate-600 md:border-b-0 md:border-r lg:overflow-auto"
        :class="sidebarOpen
          ? 'fixed inset-y-0 left-0 z-40 w-72 overflow-y-auto bg-slate-100 dark:bg-slate-800 md:static md:z-auto md:w-72 md:overflow-visible'
          : sidebarCompact
            ? 'hidden md:block md:w-20 lg:w-20'
            : 'hidden md:block md:w-72'"
      >
        <LayoutSidebar
          :compact="sidebarCompact"
          @close="closeSidebar"
          @toggle-compact="toggleSidebarCompact"
        />
      </div>

      <div class="dark:bg-slate-700 flex-1 min-h-0 min-w-0">
        <main class="p-4 pb-4 bg-slate-100 dark:bg-slate-700 overflow-x-auto lg:overflow-auto lg:h-full">
          <!-- These views handle query changes in place; remounting payroll loses the selected run. -->
          <router-view :key="['reference-view', 'bank-transactions', 'payroll-runs'].includes(route.name) ? route.path : route.fullPath"></router-view>
        </main>
      </div>
    </div>
  </div>
</template>
